import { t } from '@lingui/core/macro';
import { useEffect, useState } from 'react';

import Check from '@/assets/svg/bx-check.svg?react';
import Clone from '@/assets/svg/bx-copy.svg?react';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextArea,
} from '@/components/Form/inputs/InputGroup';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';

import { type TextAreaProps } from '../TextArea';

const CopyToClipboardTextArea = ({
  className,
  value,
  disabled,
  size = 'large',
  onFocus,
  ...rest
}: TextAreaProps) => {
  const [copied, setCopied] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  useEffect(() => {
    if (copied) {
      const timeoutId = window.setTimeout(() => {
        setCopied(false);
        setIsTooltipOpen(false);
      }, 3000);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }
  }, [copied]);

  const handleFocus: TextAreaProps['onFocus'] = (e) => {
    // Select the text so it can be easily copied
    e.target.select();
    onFocus?.(e);
  };

  const handleCopy = () => {
    if (value) {
      setCopied(true);
      navigator.clipboard.writeText(value + '');
    }
  };

  return (
    <InputGroup size={size} className={className}>
      <InputGroupTextArea
        {...rest}
        value={value}
        rows={4}
        size={size}
        disabled={disabled}
        onFocus={handleFocus}
        readOnly
      />
      <InputGroupAddon align="inline-end">
        <Tooltip open={copied || isTooltipOpen} onOpenChange={setIsTooltipOpen} disabled={disabled}>
          <TooltipTrigger
            render={
              <InputGroupButton
                className={copied ? '[&_svg]:size-7!' : ''}
                size={size === 'large' ? 'icon' : 'icon-small'}
                shape="round"
                disabled={disabled}
                aria-label={t`Copy text to clipboard`}
                onClick={handleCopy}
              >
                {copied ? <Check className="text-green-500" /> : <Clone />}
              </InputGroupButton>
            }
          />
          <TooltipContent>{copied ? t`Copied!` : t`Copy to clipboard`}</TooltipContent>
        </Tooltip>
      </InputGroupAddon>
    </InputGroup>
  );
};

export default CopyToClipboardTextArea;
